import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";

const statuses = [
  { value: "active", label: "Active" },
  { value: "exited", label: "Exited" },
  { value: "written_off", label: "Written Off" },
];

export default function InvestmentForm({ investment, investor, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    company_name: "",
    investment_amount: "",
    investment_date: "",
    ownership_percentage: "",
    status: "active",
    exit_date: "",
    exit_amount: "",
    notes: "",
  });

  useEffect(() => {
    if (investment) {
      setFormData({
        company_name: investment.company_name || "",
        investment_amount: investment.investment_amount || "",
        investment_date: investment.investment_date || "",
        ownership_percentage: investment.ownership_percentage || "",
        status: investment.status || "active",
        exit_date: investment.exit_date || "",
        exit_amount: investment.exit_amount || "",
        notes: investment.notes || "",
      });
    }
  }, [investment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      investor_id: investor.id,
      investor_name: investor.name,
      investment_amount: formData.investment_amount ? parseFloat(formData.investment_amount) : null,
      ownership_percentage: formData.ownership_percentage ? parseFloat(formData.ownership_percentage) : null,
      exit_amount: formData.exit_amount ? parseFloat(formData.exit_amount) : null,
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {investment ? "Edit Investment" : "Add Investment"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                className="h-11"
                placeholder="Company invested in"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="investment_amount">Investment Amount (NOK) *</Label>
              <Input
                id="investment_amount"
                type="number"
                value={formData.investment_amount}
                onChange={(e) => setFormData({ ...formData, investment_amount: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="investment_date">Investment Date</Label>
              <Input
                id="investment_date"
                type="date"
                value={formData.investment_date}
                onChange={(e) => setFormData({ ...formData, investment_date: e.target.value })}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ownership_percentage">Ownership (%)</Label>
              <Input
                id="ownership_percentage"
                type="number"
                step="0.01"
                max="100"
                value={formData.ownership_percentage}
                onChange={(e) => setFormData({ ...formData, ownership_percentage: e.target.value })}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.status === "exited" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="space-y-2">
                <Label htmlFor="exit_date">Exit Date</Label>
                <Input
                  id="exit_date"
                  type="date"
                  value={formData.exit_date}
                  onChange={(e) => setFormData({ ...formData, exit_date: e.target.value })}
                  className="h-11 bg-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exit_amount">Exit Amount (NOK)</Label>
                <Input
                  id="exit_amount"
                  type="number"
                  value={formData.exit_amount}
                  onChange={(e) => setFormData({ ...formData, exit_amount: e.target.value })}
                  className="h-11 bg-white"
                />
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Additional details about this investment..."
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Saving..." : investment ? "Update Investment" : "Add Investment"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}